import React, {useEffect, useMemo, useState} from 'react';
import {
    Box,
    Typography,
    IconButton,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const TransportLayerSettings = ({ items = [] }) => {
    const [list, setList] = useState(Array.isArray(items) ? items : []);
    const [open, setOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState(-1);
    const [name, setName] = useState('');
    const [url, setUrl] = useState('');
    const [dialogError, setDialogError] = useState('');

    /**
     * Return duplicate stream URLs after trimming surrounding whitespace.
     */
    const duplicateUrls = useMemo(() => {
        const seenUrls = new Set();
        const duplicates = new Set();

        list.forEach((entry) => {
            const normalizedUrl = (entry?.url || '').trim();
            if (!normalizedUrl) {
                return;
            }

            if (seenUrls.has(normalizedUrl)) {
                duplicates.add(normalizedUrl);
                return;
            }

            seenUrls.add(normalizedUrl);
        });

        return Array.from(duplicates);
    }, [list]);

    useEffect(() => {
        // Normalize incoming items if it's a JSON string
        if (!Array.isArray(items)) {
            try {
                const parsed = JSON.parse(items);
                if (Array.isArray(parsed)) setList(parsed);
            } catch (e) {
                // ignore
            }
        } else {
            setList(items);
        }
    }, [items]);

    useEffect(() => {
        // Update hidden input whenever list changes
        const hidden = document.getElementById('transport_layer_config');
        if (hidden) {
            try {
                hidden.value = JSON.stringify(list);
                // dispatch input/change events so any listeners pick up the change
                hidden.dispatchEvent(new Event('input', { bubbles: true }));
                hidden.dispatchEvent(new Event('change', { bubbles: true }));
            } catch (e) {
                console.error('Failed to update transport_layer_config hidden input', e);
            }
        }
    }, [list]);

    const openAdd = () => {
        setEditingIndex(-1);
        setName('');
        setUrl('');
        setDialogError('');
        setOpen(true);
    };

    const openEdit = (index) => {
        const it = list[index] || {name: '', url: ''};
        setEditingIndex(index);
        setName(it.name || '');
        setUrl(it.url || '');
        setDialogError('');
        setOpen(true);
    };

    const handleSave = () => {
        const entry = { name: (name || '').trim(), url: (url || '').trim() };
        if (!entry.name || !entry.url) {
            setDialogError('Both name and URL are required.');
            return;
        }

        const duplicateUrlExists = list.some((existingEntry, index) => {
            if (index === editingIndex) {
                return false;
            }

            return (existingEntry?.url || '').trim() === entry.url;
        });

        if (duplicateUrlExists) {
            setDialogError('Each Transport Layer stream must use a unique URL.');
            return;
        }

        setList(prev => {
            const copy = [...prev];
            if (editingIndex >= 0) {
                copy[editingIndex] = entry;
            } else {
                copy.push(entry);
            }
            return copy;
        });
        setDialogError('');
        setOpen(false);
    };

    const handleDelete = (index) => {
        if (!confirm('Delete this transport item?')) return;
        setList(prev => prev.filter((_, i) => i !== index));
    };

    return (
        <Box>
            <Typography variant="subtitle1" gutterBottom>Transport Layer Streams</Typography>
            {duplicateUrls.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    Transport Layer stream URLs must be unique. Duplicate URLs: {duplicateUrls.join(', ')}
                </Alert>
            )}
            <Box sx={{ mb: 1 }}>
                <Button startIcon={<AddIcon/>} variant="outlined" onClick={openAdd} size="small">Add stream</Button>
            </Box>
            <List dense>
                {list.length === 0 && (
                    <ListItem>
                        <ListItemText primary="No streams configured" />
                    </ListItem>
                )}
                {list.map((it, idx) => (
                    <ListItem key={idx} divider alignItems="flex-start">
                        <ListItemText primary={it.name || '(no name)'} secondary={it.url || ''} />
                        <ListItemSecondaryAction>
                            <IconButton edge="end" aria-label="edit" onClick={() => openEdit(idx)} size="small">
                                <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(idx)} size="small">
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </ListItemSecondaryAction>
                    </ListItem>
                ))}
            </List>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingIndex >= 0 ? 'Edit stream' : 'Add stream'}</DialogTitle>
                <DialogContent>
                    {dialogError && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                            {dialogError}
                        </Alert>
                    )}
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Name"
                        type="text"
                        fullWidth
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <TextField
                        margin="dense"
                        label="RTSP URL"
                        type="text"
                        fullWidth
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        error={Boolean(dialogError)}
                        helperText={dialogError && dialogError.toLowerCase().includes('url') ? dialogError : ''}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TransportLayerSettings;
